import { createFileRoute } from '@tanstack/react-router'
import { standingLabel } from '../shell/craidLifecycle'
import { acceptanceOf } from '../shell/planReadings'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { agreeOutcome, indicatorReadingsQuery, indicatorsQuery, outcomeWatchQuery, planQuery } from '../api/jedi'
import { useEngagement } from '../lib/engagement'
import { usePersonZone } from '../lib/personTime'
import { LoadProblem } from '../shell/LoadProblem'
import { RecordHistory } from '../shell/RecordPanel'
import { recordSearch, useLinkedRecord } from '../shell/recordLink'
import type { Indicator, IndicatorReadings, PlanNode, WatchedOutcome } from '../api/types'
import { sourceWordsList } from '../shell/sourceWords'
import { NO_EVIDENCE, evidenceOf, strengthTone, strengthWords } from '../shell/outcomeEvidence'

export const Route = createFileRoute('/_shell/outcomes')({
  component: OutcomesView,
  // A cited record's door (wave 23): the chat's record chip lands here.
  validateSearch: recordSearch,
})

/**
 * Outcomes — the record this product is built around, given a home.
 *
 * Built to brief 06 and to `Outcomes.dc.html`'s overview and record-detail
 * views. Its third view, the GATE WORKSPACE, is deliberately absent: what that
 * screen renders (validating a design package against committed measurement
 * definitions, change records with payment consequences, per-party customer and
 * contractual approvals, countersigned signoff, drafted comms, an audit trail)
 * is the full outcome-gate lifecycle, which ADR-0030 §5 defers to Tracker #95
 * and which #95's own body calls "out of Phase 1". ADR-0042 §5 enumerates it.
 * There is no disabled affordance for it either: an inert door states a shape,
 * and a greyed gate workspace would state a shape this build has decided not to
 * have yet.
 *
 * READ-ONLY BY CONSTRUCTION (C-033), like every other record surface. The
 * verbs are the record door on the history panel and, on a proposed outcome,
 * Mark as agreed (ADR-0042 §2; Erik's demo-environment rulings, 2026-10-22),
 * which drafts the agreement for an approver on Home and writes nothing.
 *
 * **The centre of this surface is a link, and the link is the point.** Brief 06:
 * *"the links are the phase-1 value: 'what threatens this outcome right now' is
 * answered by linked open risks/issues, not by an algorithm."* So the health
 * block names records and never scores them, and the words "no computed score"
 * are on the screen because they are the design's, three times over.
 */

function OutcomesView() {
  const { id: engagementId } = useEngagement()
  const plan = useQuery(planQuery(engagementId))
  const watch = useQuery(outcomeWatchQuery(engagementId))
  const indicators = useQuery(indicatorsQuery(engagementId))
  const [openId, setOpenId] = useState<string | null>(null)
  const { record } = Route.useSearch()
  useLinkedRecord(record, plan.data?.filter((n) => n.nodeType === 'outcome'), (n) => n.id, (n) => setOpenId(n.id))
  // A cited indicator lands here (UX run 08) and opens the outcome it scores.
  useLinkedRecord(record, indicators.data, (i) => i.id, (i) => {
    const scored = (i.links ?? []).find((l) => l.linkType === 'scores')?.toId
    if (scored) setOpenId(scored)
  })

  const nodes = plan.data ?? []
  const outcomes = nodes.filter((n) => n.nodeType === 'outcome')
  const readings = new Map((watch.data ?? []).map((w) => [w.outcomeId, w]))

  const measuresOf = (outcomeId: string) =>
    (indicators.data ?? []).filter((i) =>
      (i.links ?? []).some((l) => l.linkType === 'scores' && l.toId === outcomeId),
    )

  // The deliverables that enable an outcome, and what else each of them serves.
  // Bidirectional linked rows, no graph, per brief 06's contributing-scope line.
  const scopeOf = (outcomeId: string) =>
    nodes.filter(
      (n) =>
        n.nodeType === 'deliverable' &&
        (n.edges ?? []).some((e) => e.edgeType === 'enables' && e.toId === outcomeId),
    )

  const alsoServes = (deliverable: PlanNode, exceptId: string) =>
    (deliverable.edges ?? [])
      .filter((e) => e.edgeType === 'enables' && e.toId !== exceptId)
      .map((e) => outcomes.find((o) => o.id === e.toId)?.name)
      .filter((name): name is string => !!name)

  const open = openId ? outcomes.find((o) => o.id === openId) : null

  if (open) {
    return (
      <OutcomeDetail
        outcome={open}
        reading={readings.get(open.id) ?? null}
        measures={measuresOf(open.id)}
        scope={scopeOf(open.id)}
        alsoServes={(d) => alsoServes(d, open.id)}
        onBack={() => setOpenId(null)}
      />
    )
  }

  return (
    <section className="surface surface-wide">
      <header className="surface-head">
        <h1>Outcomes</h1>
        <p className="surface-sub">
          What this engagement is for, as the contract committed it. Each record carries its
          definition, what it covers, and what is linked to it right now.
        </p>
      </header>

      {plan.isPending && <p className="surface-note">Loading…</p>}
      <LoadProblem error={plan.error ?? watch.error} />
      {plan.data && outcomes.length === 0 && (
        <p className="surface-note">
          No outcomes yet. Cold start reads them from the contract, and nothing in the plan can be
          held until one exists.
        </p>
      )}

      <ul className="outcome-list">
        {outcomes.map((outcome) => {
          const reading = readings.get(outcome.id) ?? null
          const measures = measuresOf(outcome.id)
          const scope = scopeOf(outcome.id)
          return (
            <li key={outcome.id}>
              <button type="button" className="outcome-row" onClick={() => setOpenId(outcome.id)}>
                <span className="outcome-measure">
                  {measures.length === 0 ? (
                    <span className="outcome-measure-none">No measure stated</span>
                  ) : (
                    measures.slice(0, 2).map((m) => (
                      <span key={m.id} className="outcome-measure-line">
                        <span className="outcome-measure-name">{m.name}</span>
                        <span className="outcome-measure-target">{m.target ?? 'no target'}</span>
                      </span>
                    ))
                  )}
                </span>

                <span className="outcome-main">
                  <span className="outcome-name">{outcome.name}</span>
                  <span className="outcome-meta">
                    {outcome.expectedEnd ? `Due ${formatDate(outcome.expectedEnd)}` : 'No target date'}
                    {' · '}
                    {scope.length === 1
                      ? '1 deliverable under it'
                      : `${scope.length} deliverables under it`}
                  </span>
                  <HealthLine reading={reading} />
                  <EvidenceLine outcome={outcome} />
                </span>

                <span className="outcome-side">
                  <span className={`outcome-lock outcome-lock-${outcome.state ?? 'proposed'}`}>
                    {lockWords(outcome.state)}
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>

      {outcomes.length > 0 && (
        <p className="surface-note surface-note-mono">
          Drift and risk read from linked records. No computed score.
        </p>
      )}
    </section>
  )
}

/**
 * The record detail, brief 06's two columns: the committed definition on the
 * left, what is linked to it on the right, and the record's own history beneath.
 */
function OutcomeDetail({
  outcome,
  reading,
  measures,
  scope,
  alsoServes,
  onBack,
}: {
  outcome: PlanNode
  reading: WatchedOutcome | null
  measures: Indicator[]
  scope: PlanNode[]
  alsoServes: (deliverable: PlanNode) => string[]
  onBack: () => void
}) {
  const { id: engagementId } = useEngagement()
  const zone = usePersonZone()
  const queryClient = useQueryClient()
  const readings = useQuery(indicatorReadingsQuery(engagementId))
  const readingOf = (indicatorId: string) => (readings.data ?? []).find((r) => r.indicatorId === indicatorId) ?? null
  const measured = measures.some((m) => m.measure)
  // Approved and not yet agreed: the agreement is drafted for an approver, and
  // the outcome reads agreed only once one approves it on Home.
  const unagreed = (outcome.state ?? 'proposed').toLowerCase() === 'proposed'
  const agree = useMutation({
    mutationFn: () => agreeOutcome(engagementId, outcome.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['eng', engagementId, 'pending-sets'] }),
  })

  return (
    <section className="surface surface-wide">
      <button type="button" className="outcome-back" onClick={onBack}>
        ← All outcomes
      </button>

      <header className="surface-head">
        <h1>{outcome.name}</h1>
        <p className="surface-sub">
          <span className={`outcome-lock outcome-lock-${outcome.state ?? 'proposed'}`}>
            {lockWords(outcome.state)}
          </span>
          {outcome.expectedEnd && <> · Due {formatDate(outcome.expectedEnd)}</>}
        </p>
        {unagreed && (
          <div className="outcome-agree">
            {agree.isSuccess ? (
              <span className="outcome-agree-note">Waiting for approval on Home.</span>
            ) : (
              <button type="button" className="outcome-agree-button" disabled={agree.isPending}
                onClick={() => agree.mutate()}>
                {agree.isPending ? 'Drafting…' : 'Mark as agreed'}
              </button>
            )}
            {agree.isError && <span className="outcome-agree-refusal">{refusalWords(agree.error)}</span>}
          </div>
        )}
      </header>

      <div className="outcome-detail">
        <div className="outcome-definition">
          <div className="outcome-field">
            <div className="outcome-field-label">Where this record came from</div>
            <Provenance refs={outcome.sourceRefs} />
            <p className="outcome-field-note">
              Each field's own lineage is in the history below, where the change that set it is
              recorded with who approved it.
            </p>
          </div>

          <Field label="Outcome statement" body={outcome.description} />
          <Field
            label="Scope boundary"
            body={outcome.scopeBoundary}
            absent="The contract does not bound this outcome. Nothing here infers one."
          />
          <Field
            label="Target date"
            body={outcome.expectedEnd ? formatDate(outcome.expectedEnd) : null}
            absent="The contract states no date this outcome is to hold by."
          />

          <div className="outcome-field">
            <div className="outcome-field-label">Measurement definition · data sources</div>
            {measures.length === 0 ? (
              <p className="outcome-field-absent">
                No measure was stated for this outcome. It is still an outcome.
              </p>
            ) : (
              <ul className="outcome-measures">
                {measures.map((m) => (
                  <li key={m.id}>
                    <span className="outcome-measure-name">{m.name}</span>
                    <span className="outcome-measure-target">{m.target ?? 'no target stated'}</span>
                    <span className="outcome-measure-sor">
                      {m.systemOfRecord ?? 'no system of record agreed'}
                    </span>
                    {m.thresholdLanguage && (
                      <span className="outcome-measure-threshold">{m.thresholdLanguage}</span>
                    )}
                    {measurementLine(m) && <span className="outcome-measure-contract">{measurementLine(m)}</span>}
                    {m.measureWords && (
                      <span className="outcome-measure-contract">
                        Measured as {m.measureWords}
                        {m.scheduleWords ? `, read ${m.scheduleWords}` : ''}.
                      </span>
                    )}
                    {readingLine(readingOf(m.id), m.unit, zone) && (
                      <span className="outcome-measure-reading">{readingLine(readingOf(m.id), m.unit, zone)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="outcome-field-note">
              {measured ? 'Read from the customer’s ServiceNow instance.' : 'Committed definitions only.'}
            </p>
          </div>
        </div>

        <div className="outcome-linked">
          <div className="outcome-block">
            <div className="outcome-block-head">Outcome health · read from linked records</div>
            <HealthLine reading={reading} />
            <ul className="outcome-reasons">
              {(reading?.basis ?? []).map((f) => (
                <li key={`${f.kind}:${f.recordId}`}>
                  <span className="outcome-reason-kind">{f.recordType}</span>
                  <span className="outcome-reason-text">
                    {f.recordName} is {f.what}
                  </span>
                </li>
              ))}
              {(reading?.bearing ?? []).map((e) => (
                <li key={e.entryId}>
                  <span className="outcome-reason-kind">{e.type}</span>
                  <span className="outcome-reason-text">
                    {e.title}
                    {e.severity ? ` · ${e.severity}` : ''} ·{' '}
                    {/*
                      The GOVERNED label, not the schema value. This line used to
                      render `e.lifecycleStatus` verbatim, so a won't-fix issue
                      bearing on an outcome read `wont_fix` here - the same defect
                      as the work table's standing column, on a second surface, and
                      invisible for the same reason: no fixture carried a closed
                      record for it to show.
                    */}
                    {standingLabel(e.type, e.lifecycleStatus)}
                  </span>
                </li>
              ))}
              {(reading?.basis ?? []).length === 0 && (reading?.bearing ?? []).length === 0 && (
                <li className="outcome-reason-none">
                  Nothing linked to this outcome is late, and no open entry bears on it.
                </li>
              )}
            </ul>
            <p className="outcome-field-note">
              No computed score. Status, blockers and risks are traced to their records.
            </p>
          </div>

          <EvidenceBlock outcome={outcome} />

          <div className="outcome-block">
            <div className="outcome-block-head">Contributing scope</div>
            {scope.length === 0 ? (
              <p className="outcome-field-absent">
                Nothing in the plan is linked to this outcome yet, which is why it reads as not
                assessed rather than on track.
              </p>
            ) : (
              <ul className="outcome-scope">
                {scope.map((d) => {
                  const others = alsoServes(d)
                  return (
                    <li key={d.id}>
                      <span className="outcome-scope-name">{d.name}</span>
                      <span className="outcome-scope-meta">
                        {acceptanceOf(d).word}
                        {d.expectedEnd ? ` · due ${formatDate(d.expectedEnd)}` : ''}
                        {others.length > 0 ? ` · also serves ${others.join(', ')}` : ''}
                      </span>
                    </li>
                  )
                })}
              </ul>
            )}
            <p className="outcome-field-note">If this slips, what is exposed. Linked rows, no graph.</p>
          </div>

          {measures.length > 0 && (
            <div className="outcome-block">
              {/* "KPI" is permitted as a presentation label on exactly this
                  section and nowhere else (app-guidance, Erik 3 Aug): the
                  record term is Indicator. Do not "correct" this heading. */}
              <div className="outcome-block-head">Supporting KPIs · subordinate to this outcome</div>
              <ul className="outcome-kpis">
                {measures.map((m) => (
                  <li key={m.id}>
                    <span className="outcome-scope-name">{m.name}</span>
                    <span className="outcome-scope-meta">
                      {m.target ? `Committed target ${m.target}` : 'No target stated'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/*
        A SECTION, NOT A LANDING, and the one place wave 24 left as it was. The
        other four surfaces opened a 340px panel when somebody clicked a row,
        and those became the drill-down's `read` body. This is already a
        record-detail page with the outcome's fields, measures and provenance on
        it, so there is nothing to open: the history belongs in it, and the door
        stays because the conversation is not already on screen here.
      */}
      <RecordHistory
        recordId={outcome.id}
        name={outcome.name}
        door={{ scopeRef: `plan:${outcome.id}` }}
      />
    </section>
  )
}

/** A day as the reading's own span names it: the date part of its time, never moved by the browser's zone. */
const spanDay = (iso: string, back = 0) => {
  const day = new Date(`${iso.slice(0, 10)}T00:00:00Z`)
  day.setUTCDate(day.getUTCDate() - back)
  return day.toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * An Indicator's latest reading in one line (ADR-0059 decision 4): its value and the days it covers, its
 * end excluded, and, when the latest reading is a miss, why. Nothing before the first.
 */
function readingLine(r: IndicatorReadings | null, unit: string | null | undefined, zone: string): string | null {
  if (!r) return null
  const parts = [
    r.lastGood
      ? `Read ${Number(r.lastGood.value).toLocaleString()}${unit ? ` ${unit}` : ''} for ${spanDay(r.lastGood.from)} to ${spanDay(r.lastGood.to, 1)}.`
      : null,
    r.missing && r.lastMiss
      ? `Not read on ${new Date(r.lastMiss.readAt).toLocaleDateString(undefined, { timeZone: zone, month: 'short', day: 'numeric', year: 'numeric' })}. ${r.lastMiss.reason}`
      : null,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : null
}

/**
 * An Indicator's measurement contract in one line (ADR-0059 decision 1): the
 * number its readings are compared with, its unit and direction, and where it
 * stood before the work. Nothing when none of it is recorded.
 */
function measurementLine(m: Indicator): string | null {
  const parts = [
    m.targetValue != null ? `Target value ${Number(m.targetValue).toLocaleString()}${m.unit ? ` ${m.unit}` : ''}` : null,
    m.direction === 'higher_is_better' ? 'higher is better' : m.direction === 'lower_is_better' ? 'lower is better' : null,
    m.baseline
      ? `baseline ${Number(m.baseline.value).toLocaleString()} on ${new Date(`${m.baseline.asOf}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', dateStyle: 'medium' })}`
      : null,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * One committed field, in the contract's own words or honestly absent.
 *
 * **No per-field source chip, and that is a finding rather than a shortcut.**
 * Brief 06 asks for lineage per field and the design render carries four
 * different chips on this column, one per field. The build cannot produce them:
 * a plan record's `sourceRefs` is BIRTH provenance for the whole record, and a
 * later change to one field lands as its own event on the record's stream. So
 * the lineage of an individual field is real and readable, in the history panel
 * below, and is not a property of the projection. Rendering the record's ref
 * under every field would claim four sources where there is one.
 */
function Field({
  label,
  body,
  absent,
}: {
  label: string
  body: string | null | undefined
  absent?: string
}) {
  return (
    <div className="outcome-field">
      <div className="outcome-field-label">{label}</div>
      {body ? <p className="outcome-field-body">{body}</p> : (
        <p className="outcome-field-absent">{absent ?? 'Not stated in the contract.'}</p>
      )}
    </div>
  )
}

/**
 * Where the record came from, IN WORDS.
 *
 * A ref is never rendered raw. Plan records born through the door carry
 * `set:<id>` and nothing else, so a component that printed what it was given
 * would put a raw identifier on screen, which is on the banned-copy list and is
 * the exact defect wave 10 swept out of two spans on Work. An unrecognised
 * shape gets the neutral words rather than its own value, so a ref shape added
 * later cannot leak an id through this component by default.
 *
 * What it deliberately does NOT say is the document and section. That chain is
 * real but it is two hops — the record names the approved set, and the set names
 * the document and version it was read from — and only the first hop is on this
 * lens. Naming the SOW section here would mean claiming a link this projection
 * does not hold.
 */
function Provenance({ refs }: { refs: readonly string[] | null | undefined }) {
  const list = (refs ?? []).filter((r) => !!r)
  if (list.length === 0) {
    return <span className="outcome-prov outcome-prov-none">No source recorded</span>
  }
  return (
    <span className="outcome-prov-row">
      {sourceWordsList(list).map((w) => (
        <span key={w} className="outcome-prov">
          {w}
        </span>
      ))}
    </span>
  )
}

/**
 * The two strata, side by side, with the computed read never hidden behind the
 * declared one. `HealthRAG.d.ts`: the computed read is the default displayed
 * value, an override "stays visible adjacent", and a more-optimistic override
 * is flagged. That flag has a name in the component contract and it travels
 * with the rule: anti-greenwashing.
 */
function HealthLine({ reading }: { reading: WatchedOutcome | null }) {
  if (!reading) return null
  const computed = healthWords(reading.computed)
  const declared = healthWords(reading.declared)

  return (
    <span className="outcome-health">
      <span className={`outcome-dot outcome-dot-${tone(reading.computed)}`} aria-hidden="true" />
      <span className="outcome-health-computed">
        {reading.computed ? sentence(computed) : 'Not assessed'}
      </span>
      {reading.declared && (
        <span className="outcome-health-declared">
          {reading.disagrees ? `Declared ${declared}, and the records no longer say so` : `Declared ${declared}`}
        </span>
      )}
      {!reading.computed && (
        <span className="outcome-health-why">nothing in the plan is linked to it</span>
      )}
    </span>
  )
}

/**
 * The evidence, on the row, beside health and never in its place (RFC-0020).
 * Health above reads dates, so an outcome whose work is on schedule reads on
 * track with nothing to show for it; this line is what says so. Its strength
 * and the date it speaks for, or that no report on it states one.
 */
function EvidenceLine({ outcome }: { outcome: PlanNode }) {
  const evidence = evidenceOf(outcome)
  return (
    <span className="outcome-evidence">
      <span className="outcome-evidence-label">Evidence</span>
      {evidence ? (
        <>
          <span className={`outcome-strength outcome-strength-${strengthTone(evidence.strength)}`}>
            {strengthWords(evidence.strength)}
          </span>
          {evidence.asOf && <span className="outcome-evidence-when">as of {formatDate(evidence.asOf)}</span>}
        </>
      ) : (
        <span className="outcome-strength outcome-strength-none">{NO_EVIDENCE}</span>
      )}
    </span>
  )
}

/**
 * The record detail's evidence block: the evidence report's own words, how
 * strong they are, when they speak for and who said them, and the proof the
 * outcome still needs. It sits beside the health block rather than inside it,
 * because the two answer different questions: is the work on time, and has the
 * outcome been shown.
 */
function EvidenceBlock({ outcome }: { outcome: PlanNode }) {
  const evidence = evidenceOf(outcome)
  return (
    <div className="outcome-block">
      <div className="outcome-block-head">Outcome evidence · what has been shown</div>
      {evidence ? (
        <>
          <div className="outcome-evidence">
            <span className={`outcome-strength outcome-strength-${strengthTone(evidence.strength)}`}>
              {strengthWords(evidence.strength)}
            </span>
            {(evidence.asOf || evidence.by) && (
              <span className="outcome-evidence-when">
                {[evidence.asOf ? `As of ${formatDate(evidence.asOf)}` : null, evidence.by].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
          <p className="outcome-field-body">{evidence.words}</p>
          {evidence.nextProof && (
            <p className="outcome-evidence-next">
              <span className="outcome-evidence-next-label">Next proof:</span> {evidence.nextProof}
            </p>
          )}
        </>
      ) : (
        <span className="outcome-strength outcome-strength-none">{NO_EVIDENCE}</span>
      )}
    </div>
  )
}

/**
 * The governed value in the words a person reads. The record vocabulary stays
 * on-track / at-risk / off-track; this is presentation only.
 */
function healthWords(state: string | null | undefined): string {
  switch (state) {
    case 'on-track':
      return 'on track'
    case 'at-risk':
      return 'at risk'
    case 'off-track':
      return 'off track'
    default:
      return 'not assessed'
  }
}

/** The leading word of a line is capitalised; the same word mid-sentence is not. */
function sentence(words: string): string {
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function tone(state: string | null | undefined): string {
  switch (state) {
    case 'on-track':
      return 'good'
    case 'at-risk':
      return 'watch'
    case 'off-track':
      return 'bad'
    default:
      return 'none'
  }
}

/**
 * `lock_state` in the user's words. "Lock" as a bare internal label is on the
 * banned-copy list; proposed / agreed / locked are the ratified vocabulary and
 * are acceptable as copy (ADR-0042 §6).
 */
function lockWords(state: string | null | undefined): string {
  switch (state) {
    case 'locked':
      return 'Locked at a gate'
    case 'agreed':
      return 'Agreed'
    default:
      return 'Proposed'
  }
}

/** The API's own words for a refusal, unwrapped from the transport line. */
function refusalWords(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error)
  const body = /^API \d+ on \S+: (.+)$/s.exec(message)?.[1] ?? message
  try {
    const parsed: unknown = JSON.parse(body)
    if (typeof parsed === 'string') return parsed
  } catch {
    // not JSON: the words as carried
  }
  return body
}

function formatDate(iso: string): string {
  const at = new Date(`${iso}T00:00:00Z`)
  // A date, not a moment: read as UTC midnight, it is formatted as UTC too.
  // Formatted in the machine's zone, it read a day early anywhere west of UTC.
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}
